import { useLayoutEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectClassUserIdsPresentInClass,
  selectCurrentlySpeakingClassUserIds,
  selectMaxParticipantBarSlots,
  selectParticipantBarFeaturedClassUserIds,
  selectParticipantBarVisibleClassUserIds,
} from "store/selectors/pubsub-virtual-class-state";
import {
  selectClassUsersFromParticipantBarInputSorted,
  selectParticipantBarInputSource,
} from "store/selectors/virtual-class-users-sorting";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { useAv } from "utils/av";

// Keeps the visible participants list in sync with the calculated list.
export function useLocalParticipantBarUpdateObserver() {
  const dispatch = useAppDispatch();
  const { isInVirtualClass } = useAv();
  const prevSourceRef = useRef<string | null>(null);
  const maxSlots = useAppSelector(selectMaxParticipantBarSlots);
  const currentParticipantBarVisibleIds = useAppSelector(selectParticipantBarVisibleClassUserIds);
  const participantBarInputSource = useAppSelector(selectParticipantBarInputSource).source;
  const displayed = useAppSelector(selectClassUsersFromParticipantBarInputSorted);

  useLayoutEffect(() => {
    if (!isInVirtualClass && prevSourceRef.current !== null) {
      prevSourceRef.current = null;
      return undefined;
    }

    // If the source changed (for instance, in breakouts), we assume the visible participants list is no longer valid and reset it.
    const sourceChanged = prevSourceRef.current !== participantBarInputSource;
    const prevSource = prevSourceRef.current;
    prevSourceRef.current = participantBarInputSource;
    if (sourceChanged && prevSource !== null) {
      dispatch(PubsubVirtualClassStateActions.setParticipantBarVisibleClassUserIds([]));
      dispatch(PubsubVirtualClassStateActions.setParticipantBarFeaturedClassUserIds([]));
      return;
    }

    const nextParticipantBarVisibleIds = displayed.slice(0, maxSlots).map((u) => u.id);
    if (
      nextParticipantBarVisibleIds.length === currentParticipantBarVisibleIds.length &&
      nextParticipantBarVisibleIds.every(
        (id, index) => id === currentParticipantBarVisibleIds[index],
      )
    ) {
      return;
    }
    dispatch(
      PubsubVirtualClassStateActions.setParticipantBarVisibleClassUserIds(
        nextParticipantBarVisibleIds,
      ),
    );
  }, [
    isInVirtualClass,
    dispatch,
    maxSlots,
    currentParticipantBarVisibleIds,
    participantBarInputSource,
    displayed,
  ]);
}

const FEATURED_PARTICIPANTS_EXPIRATION_TIME_MS = 20 * 60 * 1000;
// Monitors the featured participants list to remove expired, absent, and speaking users.
export function useFeaturedParticipantsObserver() {
  const dispatch = useAppDispatch();
  const { isInVirtualClass } = useAv();

  const featuredParticipants = useAppSelector(selectParticipantBarFeaturedClassUserIds);
  const currentlySpeakingUserIds = useAppSelector(selectCurrentlySpeakingClassUserIds);
  const presentClassUserIds = useAppSelector(selectClassUserIdsPresentInClass);

  useLayoutEffect(() => {
    if (!isInVirtualClass || !featuredParticipants.length) return;

    const expirationTimeAgo = Date.now() - FEATURED_PARTICIPANTS_EXPIRATION_TIME_MS;
    const presentClassUserIdSet = new Set(presentClassUserIds);

    const updatedFeatured = featuredParticipants.filter((featuredUser) => {
      if (!presentClassUserIdSet.has(featuredUser.id)) {
        return false;
      }
      // currentSpeakerUserIds is updated often enough that, with a 20 minute expiration time, we don't need to set timeouts for individual users.
      if (featuredUser.timestamp < expirationTimeAgo) {
        return false;
      }
      return !currentlySpeakingUserIds.includes(featuredUser.id);
    });
    if (updatedFeatured.length !== featuredParticipants.length) {
      dispatch(
        PubsubVirtualClassStateActions.setParticipantBarFeaturedClassUserIds(updatedFeatured),
      );
    }
  }, [
    isInVirtualClass,
    featuredParticipants,
    currentlySpeakingUserIds,
    presentClassUserIds,
    dispatch,
  ]);
}
